/* eslint-disable no-bitwise */
/**
 * Remux a WebM/Matroska container holding an Opus stream into an Ogg/Opus
 * container WITHOUT re-encoding the audio.
 *
 * Why: Chrome's MediaRecorder cannot produce `audio/ogg` directly (it only
 * outputs `audio/webm`), while WhatsApp Cloud only renders an audio attachment
 * as a native voice note when it is ogg/opus. Both containers wrap the exact
 * same Opus packets, so we just unwrap them from WebM and rewrap them in Ogg.
 *
 * References:
 *  - Matroska/WebM EBML: https://www.matroska.org/technical/elements.html
 *  - Ogg encapsulation: https://datatracker.ietf.org/doc/html/rfc3533
 *  - Ogg Opus mapping:  https://datatracker.ietf.org/doc/html/rfc7845
 */

// --- EBML reader -----------------------------------------------------------

// Number of bytes in a vint, derived from the leading byte's marker bit.
const vintLength = firstByte => {
  let mask = 0x80;
  let length = 1;
  while (length <= 8 && !(firstByte & mask)) {
    mask >>= 1;
    length += 1;
  }
  return length;
};

class EbmlReader {
  constructor(arrayBuffer) {
    this.u8 = new Uint8Array(arrayBuffer);
    this.pos = 0;
    this.len = this.u8.byteLength;
  }

  // Element IDs keep their length-descriptor bits (used verbatim for matching).
  readId() {
    const length = vintLength(this.u8[this.pos]);
    let id = 0;
    for (let i = 0; i < length; i += 1) {
      id = id * 256 + this.u8[this.pos + i];
    }
    this.pos += length;
    return id;
  }

  // Sizes strip the marker bit. Returns `unknown` for streaming (all-ones) sizes.
  readSize() {
    const first = this.u8[this.pos];
    const length = vintLength(first);
    let value = first & (0xff >> length);
    let allOnes = value === (0xff >> length);
    for (let i = 1; i < length; i += 1) {
      const byte = this.u8[this.pos + i];
      value = value * 256 + byte;
      allOnes = allOnes && byte === 0xff;
    }
    this.pos += length;
    return { value, unknown: allOnes };
  }
}

// Master elements we descend into to reach CodecPrivate and the audio blocks.
const MASTER_ELEMENTS = new Set([
  0x18538067, // Segment
  0x1654ae6b, // Tracks
  0xae, // TrackEntry
  0xe1, // Audio
  0x1f43b675, // Cluster
  0xa0, // BlockGroup
]);

const ID_CODEC_PRIVATE = 0x63a2; // holds the OpusHead bytes
const ID_SIMPLE_BLOCK = 0xa3;
const ID_BLOCK = 0xa1;

// Extract the raw Opus frame from a (Simple)Block payload.
// Layout: track-number (vint) + int16 timecode + flags byte + frame data.
// Assumes no lacing (MediaRecorder writes one Opus frame per block).
const readBlockFrame = (reader, start, end) => {
  let p = start;
  p += vintLength(reader.u8[p]); // track number
  p += 2; // relative timecode
  p += 1; // flags
  return reader.u8.slice(p, end);
};

const parseWebmOpus = arrayBuffer => {
  const reader = new EbmlReader(arrayBuffer);
  let opusHead = null;
  const packets = [];

  const walk = end => {
    while (reader.pos < end && reader.pos < reader.len) {
      const id = reader.readId();
      const { value: size, unknown } = reader.readSize();
      const dataStart = reader.pos;
      const dataEnd = unknown ? end : dataStart + size;

      if (id === ID_CODEC_PRIVATE) {
        opusHead = reader.u8.slice(dataStart, dataEnd);
        reader.pos = dataEnd;
      } else if (id === ID_SIMPLE_BLOCK || id === ID_BLOCK) {
        packets.push(readBlockFrame(reader, dataStart, dataEnd));
        reader.pos = dataEnd;
      } else if (MASTER_ELEMENTS.has(id)) {
        walk(dataEnd);
        if (!unknown) reader.pos = dataEnd;
      } else {
        reader.pos = dataEnd;
      }
    }
  };

  walk(reader.len);
  return { opusHead, packets };
};

// --- Ogg muxer -------------------------------------------------------------

const writeU32LE = (arr, offset, value) => {
  arr[offset] = value & 0xff;
  arr[offset + 1] = (value >>> 8) & 0xff;
  arr[offset + 2] = (value >>> 16) & 0xff;
  arr[offset + 3] = (value >>> 24) & 0xff;
};

const writeU64LE = (arr, offset, value) => {
  let v = value;
  for (let i = 0; i < 8; i += 1) {
    arr[offset + i] = v & 0xff;
    v = Math.floor(v / 256);
  }
};

// Ogg uses CRC-32 with polynomial 0x04c11db7, no input/output reflection.
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let crc = i << 24;
    for (let k = 0; k < 8; k += 1) {
      crc = crc & 0x80000000 ? (crc << 1) ^ 0x04c11db7 : crc << 1;
    }
    table[i] = crc >>> 0;
  }
  return table;
})();

const oggCrc32 = data => {
  let crc = 0;
  for (let i = 0; i < data.length; i += 1) {
    crc = ((crc << 8) ^ CRC_TABLE[((crc >>> 24) ^ data[i]) & 0xff]) >>> 0;
  }
  return crc >>> 0;
};

// Samples (at 48kHz) encoded in a single Opus packet, from its TOC byte.
const opusPacketSamples = packet => {
  const toc = packet[0];
  const config = toc >> 3;
  const code = toc & 0x03;

  let frameMs;
  if (config < 12) {
    frameMs = [10, 20, 40, 60][config % 4];
  } else if (config < 16) {
    frameMs = [10, 20][config % 2];
  } else {
    frameMs = [2.5, 5, 10, 20][config % 4];
  }

  let frames;
  if (code === 0) frames = 1;
  else if (code === 1 || code === 2) frames = 2;
  else frames = packet[1] & 0x3f;

  return Math.round(frameMs * 48 * frames);
};

const buildOpusTags = () => {
  const encoder = new TextEncoder();
  const magic = encoder.encode('OpusTags');
  const vendor = encoder.encode('chatwoot');
  const out = new Uint8Array(magic.length + 4 + vendor.length + 4);
  let o = 0;
  out.set(magic, o);
  o += magic.length;
  writeU32LE(out, o, vendor.length);
  o += 4;
  out.set(vendor, o);
  o += vendor.length;
  writeU32LE(out, o, 0); // user comment count
  return out;
};

// Build one Ogg page carrying a single packet.
const buildPage = (payload, headerType, granule, serial, seq) => {
  const segCount = Math.floor(payload.length / 255) + 1;
  const lacing = new Uint8Array(segCount);
  let remaining = payload.length;
  for (let i = 0; i < segCount - 1; i += 1) {
    lacing[i] = 255;
    remaining -= 255;
  }
  lacing[segCount - 1] = remaining;

  const page = new Uint8Array(27 + segCount + payload.length);
  page[0] = 0x4f; // O
  page[1] = 0x67; // g
  page[2] = 0x67; // g
  page[3] = 0x53; // S
  page[4] = 0; // stream structure version
  page[5] = headerType;
  writeU64LE(page, 6, granule);
  writeU32LE(page, 14, serial);
  writeU32LE(page, 18, seq);
  // bytes 22-25 (CRC) stay zero while computing the checksum
  page[26] = segCount;
  page.set(lacing, 27);
  page.set(payload, 27 + segCount);

  writeU32LE(page, 22, oggCrc32(page));
  return page;
};

const buildOgg = (opusHead, packets) => {
  const serial = (Math.random() * 0xffffffff) >>> 0;
  const pages = [];
  let seq = 0;

  pages.push(buildPage(opusHead, 0x02, 0, serial, seq)); // BOS
  seq += 1;
  pages.push(buildPage(buildOpusTags(), 0x00, 0, serial, seq));
  seq += 1;

  let granule = 0;
  packets.forEach((packet, index) => {
    granule += opusPacketSamples(packet);
    const isLast = index === packets.length - 1;
    pages.push(buildPage(packet, isLast ? 0x04 : 0x00, granule, serial, seq));
    seq += 1;
  });

  const total = pages.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  pages.forEach(p => {
    out.set(p, offset);
    offset += p.length;
  });
  return out;
};

/**
 * Convert a WebM/Opus Blob into an Ogg/Opus Blob.
 * @param {Blob} webmBlob - audio recorded as `audio/webm;codecs=opus`.
 * @returns {Promise<Blob>} audio rewrapped as `audio/ogg`.
 */
export const webmOpusToOgg = async webmBlob => {
  const buffer = await webmBlob.arrayBuffer();
  const { opusHead, packets } = parseWebmOpus(buffer);

  if (!opusHead || packets.length === 0) {
    throw new Error('No Opus stream found in WebM container');
  }

  return new Blob([buildOgg(opusHead, packets)], { type: 'audio/ogg' });
};
